import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router';

import { DataTableProvider } from '@/shared/components/ui/data-table/context/data-table-context';
import { TData } from '@/shared/components/ui/data-table/context/data-table-context.types';
import { InnerDataTable } from '@/shared/components/ui/data-table/variant/inner-data-table/inner-data-table';
import {
  FilterScopeProvider,
  useFilterScope,
} from '@/shared/features/filter-preferences/context/filter-scope-context';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupTransformer } from '@/shared/features/groups/hooks/use-group-transformer/use-group-transformer';
import { TTransformedGroup } from '@/shared/features/groups/hooks/use-group-transformer/use-group-transformer.types';
import { useGroupedResourceQueryParams } from '@/shared/features/groups/hooks/use-grouped-resource-query-params/use-grouped-resource-query-params';
import { useSubgroupListDefinitions } from '@/shared/features/groups/hooks/use-subgroup-list-definition/use-subgroup-list-definition';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import { useReadSubGroupsForResourceGroupQuery } from '@/shared/redux/api/assets/groups/groups-api';

const SubgroupsListInner = () => {
  const { isReady } = useFilterScope();
  const { groupId } = useParams();
  const { resourceLists, groupRoutePath, permissionActions } =
    useGroupsConfig();
  const { canAny } = usePermissionCheck();
  const canManageSubgroups = canAny([
    permissionActions.updateGroup,
    permissionActions.deleteGroup,
  ]);
  const listConfig = resourceLists?.subGroups;
  const colDefs = useSubgroupListDefinitions({
    contextMenuRenderer: listConfig?.rowActions,
  });
  const { pagination } = useGroupedResourceQueryParams();
  const { transformGroup } = useGroupTransformer();
  const { data, isLoading, isFetching } = useReadSubGroupsForResourceGroupQuery(
    { groupId: groupId!, pagination },
    { skip: !isReady || !groupId },
  );
  const rowData = data?.readSubGroupsForResourceGroup.items.map(transformGroup);

  const getItemLink = (item: TTransformedGroup) =>
    listConfig?.getItemLink?.(groupId ?? '', item.groupId) ??
    `${groupRoutePath}/${item.groupId}`;

  const getBatchActions = () => {
    const render = listConfig?.renderBatchActions;
    if (!render || !canManageSubgroups) {
      return undefined;
    }

    return (
      selectedItems: TTransformedGroup[],
      setSelectedItems: Dispatch<SetStateAction<TTransformedGroup[]>>,
    ) =>
      render(
        selectedItems.map(({ raw }) => raw),
        () => setSelectedItems([]),
      );
  };

  const _data: TData<TTransformedGroup> = {
    isFetching,
    isLoading,
    items: rowData || [],
    pages: data?.readSubGroupsForResourceGroup.pages ?? 0,
    itemCount: data?.readSubGroupsForResourceGroup.itemCount ?? 0,
  };

  return (
    <DataTableProvider<TTransformedGroup>
      value={{
        data: _data,
        getItemId: (item: TTransformedGroup) => item.groupId,
        getItemLink,
      }}
      colDefs={colDefs}
    >
      <InnerDataTable<TTransformedGroup> batchActions={getBatchActions()} />
    </DataTableProvider>
  );
};

export const SubgroupsList = () => (
  <FilterScopeProvider scopeKey="group.subgroups.list">
    <SubgroupsListInner />
  </FilterScopeProvider>
);
